import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Spin, Button, Tag, Empty } from "antd";
import {
  CalendarOutlined,
  EnvironmentOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import styled from "styled-components";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px 20px;
`;

const ContentCard = styled.div`
  max-width: 800px;
  margin: 0 auto;
  background: white;
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
`;

const EventHeader = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 40px;
  color: white;
  text-align: center;
`;

const CompanyName = styled.h3`
  color: white;
  font-size: 18px;
  margin-bottom: 10px;
  opacity: 0.9;
`;

const EventTitle = styled.h1`
  color: white;
  font-size: 36px;
  font-weight: 700;
  margin: 0;
`;

const EventImage = styled.div`
  width: 100%;
  height: 400px;
  background-color: #f0f0f0;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EventContent = styled.div`
  padding: 40px;
`;

const EventDetail = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  padding: 16px;
  background-color: #f9f9f9;
  border-radius: 8px;
  border-left: 4px solid #667eea;

  .anticon {
    font-size: 24px;
    color: #667eea;
    margin-right: 16px;
    margin-top: 4px;
  }
`;

const DetailContent = styled.div`
  flex: 1;
`;

const DetailLabel = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 14px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
`;

const DetailValue = styled.div`
  color: #666;
  font-size: 18px;
  line-height: 1.6;
`;

const Section = styled.div`
  margin-top: 32px;
  padding-top: 32px;
  border-top: 1px solid #e0e0e0;
`;

const SectionTitle = styled.h3`
  color: #667eea;
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  
  .anticon {
    margin-right: 10px;
  }
`;

const SectionContent = styled.p`
  color: #666;
  font-size: 16px;
  line-height: 1.8;
  white-space: pre-wrap;
`;

const StatusTag = styled(Tag)`
  font-size: 14px;
  padding: 6px 16px;
  border-radius: 20px;
  margin-top: 10px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const ErrorContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ErrorCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 60px 40px;
  text-align: center;
  max-width: 500px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
`;

const Footer = styled.div`
  text-align: center;
  padding: 32px;
  background-color: #f9f9f9;
  color: #999;
  font-size: 14px;
`;

const PublicEventView = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${BASE_URL}/companies/events/${slug}/public`
      );
      setEvent(response.data.data);
      setError(null);
    } catch (error) {
      console.error("Error fetching event:", error);
      setError(error.response?.data?.detail || "Event not found");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    return date.toLocaleDateString('en-US', options);
  };

  const formatAmount = (amount, currencyCode) => {
    return `${currencyCode} ${amount.toLocaleString()}`;
  };

  const isUpcoming = event && new Date(event.event_date) > new Date();
  const isPast = event && new Date(event.event_date) < new Date();

  if (loading) {
    return (
      <LoadingContainer>
        <Spin size="large" tip="Loading event details..." />
      </LoadingContainer>
    );
  }

  if (error || !event) {
    return (
      <ErrorContainer>
        <ErrorCard>
          <Empty
            description={
              <div>
                <h2 style={{ color: "#333", marginTop: "20px" }}>
                  Event Not Found
                </h2>
                <p style={{ color: "#666", fontSize: "16px" }}>
                  {error || "The event you're looking for doesn't exist or is no longer available."}
                </p>
              </div>
            }
          />
        </ErrorCard>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <ContentCard>
        <EventHeader>
          <CompanyName>{event.company_name}</CompanyName>
          <EventTitle>{event.name}</EventTitle>
          {isUpcoming && <StatusTag color="green">Upcoming Event</StatusTag>}
          {isPast && <StatusTag color="default">Past Event</StatusTag>}
        </EventHeader>

        {event.image_url && (
          <EventImage>
            <img src={event.image_url} alt={event.name} />
          </EventImage>
        )}

        <EventContent>
          <EventDetail>
            <CalendarOutlined />
            <DetailContent>
              <DetailLabel>Event Date</DetailLabel>
              <DetailValue>{formatDate(event.event_date)}</DetailValue>
            </DetailContent>
          </EventDetail>

          {event.physical_location && (
            <EventDetail>
              <EnvironmentOutlined />
              <DetailContent>
                <DetailLabel>Location</DetailLabel>
                <DetailValue>{event.physical_location}</DetailValue>
              </DetailContent>
            </EventDetail>
          )}

          <EventDetail>
            <DetailContent>
              <DetailLabel>Price Per Person</DetailLabel>
              <DetailValue>
                {formatAmount(event.amount, event.currency_code || "KES")}
              </DetailValue>
            </DetailContent>
          </EventDetail>

          {event.description && (
            <Section>
              <SectionTitle>
                <InfoCircleOutlined />
                About This Event
              </SectionTitle>
              <SectionContent>{event.description}</SectionContent>
            </Section>
          )}

          {event.payment_info && (
            <Section>
              <SectionTitle>
                Payment Information
              </SectionTitle>
              <SectionContent>{event.payment_info}</SectionContent>
            </Section>
          )}
        </EventContent>

        <Footer>
          <p>
            Event hosted by <strong>{event.company_name}</strong>
          </p>
        
        </Footer>
      </ContentCard>
    </PageContainer>
  );
};

export default PublicEventView;